import React, {
  // lazy,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Outlet,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { retryLazy as lazy } from "./utils/retryLazy";
import { useDispatch, useSelector } from "react-redux";
//
import { DefaultLayout, MobileLayout } from "./layouts";
import { Preloader } from "./components";
import ModuleHomeRedirect from "./components/ModuleHomeRedirect";
import ScrollToTop from "./helpers/scroll-top";
import { ExpiredCode } from "./ExpiredCode.js";
import { AuthSessionHandler } from "./features/auth/AuthSessionHandler.jsx";
import { setPermissions, setEmployeeInfo } from "./store/slice/user-slice.js";
import { setMobileHelper } from "@store/slice/mobile-helper-slice.js";
import { handleLogout } from "./features/auth/authActions.js";
import { savePostLoginRedirect } from "./helpers/loginRedirect";
import { logMobileBranchDebug } from "@helpers/mobileBranchDebug";

// 引入增強的錯誤處理元件
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import OfflineDetector from "./components/OfflineDetector/OfflineDetector";

import HeadMeta from "./HeadMeta.js";

// 導入 react-lazy-load-image-component 的模糊效果樣式
import 'react-lazy-load-image-component/src/effects/blur.css';

const Login = lazy(() => import("./pages/Login"));
const Overview = lazy(() => import("./pages/Overview"));
// const Scan = lazy(() => import('./pages/Scan'));
// const ScanNew = lazy(() => import("./pages/Scan/ScanNew.js"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Invoice = lazy(() => import("./pages/Invoice"));
const CreateInvoice = lazy(() => import("./pages/Invoice/create/CreateInvoice.js"));
const Statistics = lazy(() => import("./pages/Statistics"));
const StatisticsIndex = lazy(() => import("./pages/Statistics/StatisticsIndex"));
// 訂單業績明細
const OrderPerformanceDetails = lazy(() => import("./pages/Statistics/pages/OrderPerformanceDetails"));
const ConsultantPerformanceNew = lazy(() => import("./pages/Statistics/pages/ConsultantPerformanceNew.js"));
const ReportWithMemberSidebar = lazy(() => import("./pages/Statistics/pages/ReportWithMemberSidebar.jsx"));
const PersonalProfit = lazy(() => import("./pages/Statistics/pages/PersonalProfit.js"));
const PerformanceBonusTrial = lazy(() => import("./pages/Statistics/pages/PerformanceBonusTrial.js"));
const CommentAnalysis = lazy(() => import("./pages/Statistics/pages/CommentAnalysis.js"));
const VerificationReport = lazy(() => import("./pages/Statistics/pages/VerificationReport.js"));
const LedgerReport = lazy(() => import("./pages/Statistics/pages/LedgerReport.js"));
const VirtualPerformanceReport = lazy(() => import("./pages/Statistics/pages/VirtualPerformanceReport.js"));
const Helper = lazy(() => import("./pages/Helper/Helper.js"));
const Sales = lazy(() => import("./pages/Sales"));
const SalesIndex = lazy(() => import("./pages/Sales/SalesIndex"));
const Appointment = lazy(() => import("./pages/Appointment"));
const AppointmentIndex = lazy(() => import("./pages/Appointment/AppointmentIndex"));
const AccountingReport = lazy(() => import("./pages/Appointment/pages/AccountingReport.js"));
const Tickets = lazy(() => import("./pages/Tickets"));
const TicketsIndex = lazy(() => import("./pages/Tickets/TicketsIndex"));
const ShiftSchedule = lazy(() => import("./pages/ShiftSchedule"));
const ShiftScheduleIndex = lazy(() => import("./pages/ShiftSchedule/ShiftScheduleIndex.js"));
const Customer = lazy(() => import("./pages/Customer"));
const CustomerIndex = lazy(() => import("./pages/Customer/CustomerIndex.js"));
const Options = lazy(() => import("./pages/Options"));
const OptionsIndex = lazy(() => import("./pages/Options/OptionsIndex.js"));
const Develop = lazy(() => import("./pages/Develop/Develop.js"));
const SoapConsultationBatchPrintDev = process.env.NODE_ENV === "development"
  ? lazy(() => import("./pages/Develop/SoapConsultationBatchPrintDev.jsx"))
  : null;
const Chart = lazy(() => import("./pages/Helper/components/Chart"));
const MemberSearchPage = lazy(() => import("./pages/Mobile/MemberSearchPage"));
const MemberNewPage = lazy(() => import("./pages/Mobile/MemberNewPage"));
const MemberProfilePage = lazy(() => import("./pages/Mobile/MemberProfilePage"));

const PlanFlow = lazy(() => import("./pages/PlanFlow"));
// const ServicePlan = lazy(() => import("./pages/ServicePlan/ServicePlan.js"));
const BillDetail = lazy(() => import("./pages/BillDetail"));
const BillDetailContent = lazy(() => import("./pages/BillDetailContent"));
const ServiceManage = lazy(() => import("./pages/ServiceManage"));
const SetCreditCard = lazy(() => import("./pages/SetPaymentMethod/components/setCreditCard.js"));
const BranchInfo = lazy(() => import("./pages/ShopInfo/ShopInfo.js"));
const SetInvoice = lazy(() => import("./components/SetInvoice/SetInvoice.js"));
const TestPrintReceipt = lazy(() => import("./pages/TestPages/PrintTest/TestPrintReceipt"));
const TestPrintReceiptContent = lazy(() => import("./pages/TestPages/PrintTest/TestPrintReceiptContent"));
const AccountingWrapper = lazy(() => import("./pages/Accounting/AccountingWrapper.js"));
const Accounting = lazy(() => import("./pages/Accounting/Accounting.js"));
const Close = lazy(() => import("./pages/Accounting/Close/Close.js"));
const CloseRecord = lazy(() => import("./pages/Accounting/CloseRecord/CloseRecord.js"));
const OutstandingDetails = lazy(() => import("./pages/Accounting/OutstandingDetails/OutstandingDetails.js"));
const CashWithdraw = lazy(() => import("./pages/Accounting/CashWithdraw/CashWithdraw.js"));
const AccountAudit = lazy(() => import("./pages/Accounting/AccountAudit/AccountAudit.js"));
const Pims = lazy(() => import("./pages/Pims"));
const ConsentEditor = lazy(() => import("./pages/ShiftSchedule/pages/ConsentEditor"));
const ConsentServiceEdit = lazy(() => import("./pages/ShiftSchedule/pages/ConsentServiceEdit"));
const ConsentCategoryEdit = lazy(() => import("./pages/ShiftSchedule/pages/ConsentCategoryEdit"));
const PaymentConsentServiceEdit = lazy(() => import("./pages/ShiftSchedule/pages/PaymentConsentServiceEdit"));
const PaymentConsentCategoryEdit = lazy(() => import("./pages/ShiftSchedule/pages/PaymentConsentCategoryEdit"));
const QuestionnaireEditor = lazy(() => import("./pages/ShiftSchedule/pages/QuestionnaireEditor"));
const QuestionnaireServiceEdit = lazy(() => import("./pages/ShiftSchedule/pages/QuestionnaireServiceEdit"));
const QuestionnaireCategoryEdit = lazy(() => import("./pages/ShiftSchedule/pages/QuestionnaireCategoryEdit"));
const ConsultantRecordShell = lazy(() => import("./pages/Customer/pages/ConsultantRecordShell.jsx"));
const ConsultantRecordLayout = lazy(() => import("./pages/Customer/pages/ConsultantRecordLayout.jsx"));
const SoapRecordLayout = lazy(() => import("./pages/Customer/pages/SoapRecordLayout.jsx"));
// const PurchaseRequests = lazy(() => import("./pages/Pims/PurchaseRequests"));
// const PurchaseOrders = lazy(() => import("./pages/Pims/PurchaseOrders"));
// const AcceptanceRecords = lazy(() => import("./pages/Pims/AcceptanceRecords"));
// const VendorPrices = lazy(() => import("./pages/Pims/VendorPrices"));
// const Vendors = lazy(() => import("./pages/Pims/Vendors"));
// const Purchasers = lazy(() => import("./pages/Pims/Purchasers"));

// const InventoryList = lazy(() => import("./Inventory/InventoryList"));
// const InventoryRecords = lazy(() => import("./Inventory/InventoryRecords"));
// const Warehouse = lazy(() => import("./Inventory/Warehouse"));

// 自定義 SafeSuspense 包裝器，確保始終使用 ErrorBoundary 包裝懶載入元件
const SafeSuspense = ({ children, fallback }) => (
  <ErrorBoundary>
    <Suspense fallback={fallback || <Preloader />}>{children}</Suspense>
  </ErrorBoundary>
);

/** 主版面常駐；Suspense 只包住 Outlet，避免 lazy 母頁面載入時卸載側邊欄 */
function DefaultLayoutShell() {
  return (
    <DefaultLayout>
      <SafeSuspense fallback={<Preloader />}>
        <Outlet />
      </SafeSuspense>
      <RedirectOnMobile />
    </DefaultLayout>
  );
}

function LazyRoute({ component: Component }) {
  return (
    <SafeSuspense fallback={<Preloader />}>
      <Component />
    </SafeSuspense>
  );
}

const MOBILE_ROUTE_MAX_WIDTH_PX = 600;

function getRouteViewportWidth() {
  return Math.round(
    window.visualViewport?.width ||
      document.documentElement?.clientWidth ||
      window.innerWidth
  );
}

function RedirectOnMobile() {
  const [windowWidth, setWindowWidth] = useState(getRouteViewportWidth);
  const navigate = useNavigate();
  const status = useSelector((state) => state.user.user.status);
  const rehydrated = useSelector((state) => state._persist?.rehydrated);
  const location = useLocation();

  useEffect(() => {
    let rafId = 0;
    let settleTimerId = 0;

    const handleResize = () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimerId);

      rafId = requestAnimationFrame(() => {
        setWindowWidth(getRouteViewportWidth());
      });

      // 手機旋轉時 viewport 數值可能晚一點穩定，再同步一次避免停在舊版型。
      settleTimerId = window.setTimeout(() => {
        setWindowWidth(getRouteViewportWidth());
      }, 160);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimerId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // 須等 redux-persist 還原完成，避免新分頁開啟深連結時誤判未登入
    if (!rehydrated) return;

    if (status === false) {
      if (location.pathname !== "/") {
        savePostLoginRedirect(location.pathname, location.search);
        return navigate("/");
      }
      return;
    }
    if (windowWidth <= MOBILE_ROUTE_MAX_WIDTH_PX) {
      if (!location.pathname.startsWith("/m")) {
        navigate("/m");
      }
    } else if (location.pathname.startsWith("/m")) {
      navigate("/appointment");
    }
  }, [
    rehydrated,
    windowWidth,
    navigate,
    status,
    location.pathname,
    location.search,
  ]);

  return null;
}
//
function App() {
  const dispatch = useDispatch();
  // const token = useSelector((state) => state.user.user.token);
  const userInfo = useSelector((state) => state.user.user);
  // const permissions = useSelector(state => state.user.user.permissions);
  // expiredCode(token, dispatch); // 處理過期 token
  useEffect(() => {
    if (!userInfo.brandInfo) {
      handleLogout(dispatch);
      // navigate("/");
    }
  }, [dispatch, userInfo.brandInfo]);

  useEffect(() => {
    if (!userInfo.permissions) {
      handleLogout(dispatch);
    }
  }, [dispatch, userInfo]);

  // 自動登入開啟時從 localStorage 取得該帳號的 permissions，否則點擊 side bar 會出現權限不足彈窗
  // 依賴檔案 : Login.js(自動登入)
  // 依賴檔案 : UnLogin.js 在此將 permissions 存到 localStorage
  // 20241218 趕著demo 硬加入 自動登入取得小幫手資料
  useEffect(() => {
    const currentPermissions = userInfo.permissions;
    const hasValidPermissions = currentPermissions
      && !Array.isArray(currentPermissions)
      && typeof currentPermissions === 'object'
      && Object.keys(currentPermissions).length > 0;

    // 只在 redux-persist 未恢復有效權限時，才從 localStorage 補充
    if (!hasValidPermissions) {
      const getRevxPermissions = JSON.parse(localStorage.getItem("permissions"));
      if (getRevxPermissions) {
        dispatch(setPermissions({ permissions: getRevxPermissions }));
      }
    }

    const getHelper = JSON.parse(localStorage.getItem("eternalHelper"));
    if (getHelper) {
      const revxToken = localStorage.getItem("revxToken");
      const storedEmployeeInfo = JSON.parse(
        localStorage.getItem("eternalEmployeeInfo") || "null"
      );
      const mergedHelper = {
        ...getHelper,
        data: revxToken || getHelper.data,
        employeeInfo: {
          ...(getHelper.employeeInfo || {}),
          ...(storedEmployeeInfo || {}),
          employeeBranchId:
            storedEmployeeInfo?.employeeBranchId ??
            getHelper.employeeInfo?.employeeBranchId ??
            getHelper.branchId ??
            null,
        },
      };

      logMobileBranchDebug(
        "app-bootstrap-helper-state",
        {
          source: "app-bootstrap",
          token: mergedHelper.data,
          uiBranchId: mergedHelper.employeeInfo?.employeeBranchId ?? mergedHelper.branchId ?? null,
          helperBranchId: mergedHelper.employeeInfo?.employeeBranchId ?? mergedHelper.branchId ?? null,
          extra: {
            originalHelperBranchId: getHelper?.employeeInfo?.employeeBranchId ?? getHelper?.branchId ?? null,
            mergedFromRevxToken: Boolean(revxToken),
            mergedEmployeeBranchId: storedEmployeeInfo?.employeeBranchId ?? null,
          },
        },
        "info"
      );

      dispatch(setMobileHelper(mergedHelper));
    }
  }, [dispatch]);
  //  20250212 自動登入時取得 employee info -> 關帳要用
  useEffect(() => {
    const employeeInfo = JSON.parse(
      localStorage.getItem("eternalEmployeeInfo")
    );
    // console.log("test")
    // console.log({ employeeInfo });
    if (employeeInfo) {
      dispatch(setEmployeeInfo({ employeeInfo: employeeInfo }));
    }
  }, [dispatch]);

  return (
    <Router>
      <HeadMeta />
      <ErrorBoundary>
        <OfflineDetector />
        <ScrollToTop>
          <ExpiredCode />
          <AuthSessionHandler />
          <Routes>
            <Route
              path="/"
              element={
                <SafeSuspense fallback={<Preloader />}>
                  <Login />
                </SafeSuspense>
              }
            />
            <Route element={<DefaultLayoutShell />}>
                {/* <Route path="/overview" element={<Overview />} /> */}
                {/* <Route path="/invoice" element={<Invoice />} /> */}
                {/* <Route path="/invoice/create" element={<CreateInvoice />} /> */}

                {/* 數據 */}
                <Route path="/statistics" element={<Statistics />}>
                  {/* 數據 > 首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ModuleHomeRedirect sidebarName="statistics" />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 業績報表 */}
                  <Route path="performanceReport" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ReportWithMemberSidebar />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 虛擬業績報表 */}
                  <Route path="virtualPerformanceReport" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <VirtualPerformanceReport />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 核銷明細報表 */}
                  <Route path="verificationReport" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <VerificationReport />
                    </SafeSuspense>
                  } />
                  <Route path="ledgerReport" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <LedgerReport />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 諮詢師業績 */}
                  <Route path="consultantPerformance" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ConsultantPerformanceNew />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 訂單業績明細 */}
                  <Route path="orderPerformanceDetails" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <OrderPerformanceDetails />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 個人分潤 */}
                  <Route path="personalProfit" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <PersonalProfit />
                    </SafeSuspense>
                  } />
                  {/* 數據 > 業績獎金級距試算 */}
                  <Route path="performanceBonusTrial" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <PerformanceBonusTrial />
                    </SafeSuspense>
                  } />
                  {/* ---------- 以下未出現 ---------- */}
                  {/* 數據 > 業績報表 > 顧問業績 */}
                  <Route path=":pagination/*" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <StatisticsIndex />
                    </SafeSuspense>
                  } />
                  {/* 評論分析的子路由 */}
                  <Route path="commentAnalysis" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <CommentAnalysis />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 銷售 */}
                <Route path="/sales" element={<Sales />}>
                  {/* 銷售 > 銷售首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <SalesIndex />
                    </SafeSuspense>
                  } />

                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <SalesIndex />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 會員 */}
                <Route path="/customer" element={<Customer />}>
                  {/* 會員 > 會員首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ModuleHomeRedirect sidebarName="customer" />
                    </SafeSuspense>
                  } />

                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <CustomerIndex />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 預約 */}
                <Route path="/appointment" element={<Appointment />}>
                  {/* 預約 > 預約首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <AppointmentIndex />
                    </SafeSuspense>
                  } />
                  {/* 預約 > 預約列表 */}
                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <AppointmentIndex />
                    </SafeSuspense>
                  } />
                  {/* 預約 > 關帳紀錄 */}
                  <Route path="accounting-record/:id" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <AccountingReport />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 課程 */}
                <Route path="/tickets" element={<Tickets />}>
                  {/* 課程 > 課程首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ModuleHomeRedirect sidebarName="tickets" />
                    </SafeSuspense>
                  } />
                  {/* 課程 > 列表 */}
                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <TicketsIndex />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 班表 */}
                <Route path="/shiftschedule" element={<ShiftSchedule />}>
                  {/* 班表 > 班表首頁 */}
                  <Route index element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ModuleHomeRedirect sidebarName="shiftschedule" />
                    </SafeSuspense>
                  } />
                  {/* 班表 > 班表列表 */}
                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ShiftScheduleIndex />
                    </SafeSuspense>
                  } />
                  {/* 班表 > 表單建立 */}
                  <Route path="consent-editor/:templateId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ConsentEditor />
                    </SafeSuspense>
                  } />
                  {/* 班表 > 表單編輯 */}
                  <Route path="consent-service-edit/:serviceId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ConsentServiceEdit />
                    </SafeSuspense>
                  } />
                  <Route path="consent-category-edit/:categoryId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ConsentCategoryEdit />
                    </SafeSuspense>
                  } />
                  <Route path="payment-consent-service-edit/:serviceId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <PaymentConsentServiceEdit />
                    </SafeSuspense>
                  } />
                  <Route path="payment-consent-category-edit/:categoryId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <PaymentConsentCategoryEdit />
                    </SafeSuspense>
                  } />
                  <Route path="questionnaire-editor/:templateId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <QuestionnaireEditor />
                    </SafeSuspense>
                  } />
                  <Route path="questionnaire-service-edit/:serviceId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <QuestionnaireServiceEdit />
                    </SafeSuspense>
                  } />
                  <Route path="questionnaire-category-edit/:categoryId" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <QuestionnaireCategoryEdit />
                    </SafeSuspense>
                  } />
                </Route>
                {/* 帳務 */}
                <Route path="/accounting/*" element={<AccountingWrapper />}>
                  <Route index element={<Accounting />} />
                  <Route path="close" element={<Close />} />
                  <Route path="close-record" element={<CloseRecord />} />
                  <Route path="outstanding-details" element={<OutstandingDetails />} />
                  <Route path="cash-withdraw" element={<CashWithdraw />} />
                  <Route path="account-audit" element={<AccountAudit />} />
                  <Route path="*" element={<Navigate to="/accounting" replace />} />
                </Route>
                {/* 設定 */}
                <Route path="/options" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <Options />
                  </SafeSuspense>
                }>
                  <Route
                    index
                    element={
                      <SafeSuspense fallback={<Preloader />}>
                        <ModuleHomeRedirect sidebarName="options" />
                      </SafeSuspense>
                    }
                  />
                  <Route
                    path=":pagination"
                    element={
                      <SafeSuspense fallback={<Preloader />}>
                        <OptionsIndex />
                      </SafeSuspense>
                    }
                  />
                  <Route
                    path=":pagination/:branchId"
                    element={<BranchInfo />}
                  />
                </Route>
                <Route path="/helper" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <Helper />
                  </SafeSuspense>
                } />

                <Route
                  element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ModuleHomeRedirect sidebarName="options" />
                    </SafeSuspense>
                  } />
                  <Route path=":pagination" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <OptionsIndex />
                    </SafeSuspense>
                  } />
                  <Route path=":pagination/:branchId" element={<BranchInfo />} />
                {/* 請採驗 */}
                <Route path="/pims/*" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <Pims />
                  </SafeSuspense>
                } />
                </Route>










                <Route element={
                  <SafeSuspense fallback={<Preloader />}>
                    <ConsultantRecordShell />
                  </SafeSuspense>
                }>
                  <Route path="/:branchId/member/consultantRecord/:chartNumber" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <ConsultantRecordLayout />
                    </SafeSuspense>
                  } />
                  <Route path="/:branchId/member/soapRecord/:chartNumber" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <SoapRecordLayout />
                    </SafeSuspense>
                  } />
                </Route>

                <Route path="/options/serviceplan/manage" element={<LazyRoute component={ServiceManage} />} />
                <Route path="/options/setPaymentMethod/creditCard" element={<LazyRoute component={SetCreditCard} />} />
                <Route path="/options/planflow" element={<LazyRoute component={PlanFlow} />} />
                <Route path="/options" element={<LazyRoute component={Options} />} />
                <Route path="/options/setInvoice" element={<LazyRoute component={SetInvoice} />} />
                <Route path="/options/serviceplan/bill" element={<LazyRoute component={BillDetail} />} />
                <Route path="/options/serviceplan/bill/:billId" element={<LazyRoute component={BillDetailContent} />} />
                {process.env.NODE_ENV === "development" && SoapConsultationBatchPrintDev && (
                  <Route path="/dev/soap-batch-print" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <SoapConsultationBatchPrintDev />
                    </SafeSuspense>
                  } />
                )}
                {/* 列印測試頁面 */}
                {/* <Route path="/test" element={<TestPrintReceipt />}>
                  <Route path="print-receipt" element={
                    <SafeSuspense fallback={<Preloader />}>
                      <TestPrintReceiptContent />
                    </SafeSuspense>
                  } />
                </Route> */}
                <Route path="/404" element={<LazyRoute component={NotFound} />} />
                <Route path="*" element={<LazyRoute component={NotFound} />} />
              

              {/* ---------------------------------------------- 手機版 ---------------------------------------------- */}
              <Route path="/m" element={
                <MobileLayout>
                  <RedirectOnMobile />
                </MobileLayout>
              }>
                <Route index element={<Navigate to="app" replace />} />
                <Route path="app" element={<Helper />} />
                <Route path="chart" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <Chart />
                  </SafeSuspense>
                } />
                <Route path="member" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <MemberSearchPage />
                  </SafeSuspense>
                } />
                <Route path="member/:userId" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <MemberProfilePage />
                  </SafeSuspense>
                } />
                <Route path="member/new" element={
                  <SafeSuspense fallback={<Preloader />}>
                    <MemberNewPage />
                  </SafeSuspense>
                } />
              </Route>
            </Routes>
        </ScrollToTop>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
